import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import CommonSelect from "../../../../core/common/commonSelect";
import { StudentDetailsOutletContext } from "./StudentDetailsLayout";
import { getAllAllocations, TransportAllocation } from "../../../../service/transportAssignVehicles";
import { FeeRecord, getAllFeeType } from "../../../../service/fees";
import { DiscountData, getDiscountByEnrollment } from "../../../../service/discount";
import { updateEnrollmentProfile } from "../../../../service/enrollment";
import { getAllClasses } from "../../../../service/classes";
import { listGroups } from "../../../../service/group";
import { selectUser } from "../../../../core/data/redux/slice/user";
import { getAreas } from "../../../../service/transportAreas";

type SelectOption = { value: string; label: string };

const StudentDetails = () => {
  const { enrollment: data, refetch } = useOutletContext<StudentDetailsOutletContext>();
  const currentUser = useSelector(selectUser);
  const [transportAllocation, setTransportAllocation] = useState<TransportAllocation | null>(null);
  const [loadingTransport, setLoadingTransport] = useState(false);
  const [feeTypes, setFeeTypes] = useState<FeeRecord[]>([]);
  const [discount, setDiscount] = useState<DiscountData | null>(null);
  const [loadingAcademicDetails, setLoadingAcademicDetails] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [sectionOptions, setSectionOptions] = useState<SelectOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<SelectOption[]>([]);
  const [areaOptions, setAreaOptions] = useState<SelectOption[]>([]);
  const enrollmentId = data?._id;
  const studentId = data?.student?._id;
  const classId = data?.class?._id;
  const sessionId = data?.session?._id;
  const systemId = data?.system?._id;
  // 2026-09-30: was hardcoded to admin/owner only, bypassing the permissions
  // system entirely -- moved to a real, admin-manageable "edit" action on the
  // Student module (Cashier granted by default), same pattern as every other
  // permission check in the app.
  const canEditProfile =
    currentUser.permissions?.includes("student.edit") || !currentUser.permissions?.length;

  useEffect(() => {
    const fetchTransportAllocation = async () => {
      if (!enrollmentId) {
        setTransportAllocation(null);
        return;
      }

      setLoadingTransport(true);
      try {
        const response = await getAllAllocations({
          studentId: studentId || undefined,
          page: 1,
          limit: 10,
        });

        const list = response.data || [];
        const currentEnrollmentId = enrollmentId;
        const matchedByEnrollment = list.find((item: any) => {
          const itemEnrollmentId =
            item.enrollment?._id ||
            item.enrollmentId ||
            (typeof item.enrollment === "string" ? item.enrollment : null);
          return itemEnrollmentId === currentEnrollmentId;
        });
        const allocation: any = matchedByEnrollment ?? list[0];

        if (allocation) {
          const driver: any = allocation.driver || {};
          setTransportAllocation({
            _id: allocation._id,
            student: allocation.student || {},
            enrollmentId: allocation.enrollment?._id || allocation.enrollmentId || enrollmentId,
            van: allocation.van,
            driver: driver,
            driverImg: driver.driverImg || driver.img || allocation.driverImg || "",
            pickupStop: allocation.pickupStop,
            startDate: allocation.startDate,
            status: allocation.status,
            branch: allocation.branch,
          } as TransportAllocation);
        } else {
          setTransportAllocation(null);
        }
      } catch (error) {
        console.error("Error fetching transport allocation:", error);
        setTransportAllocation(null);
      } finally {
        setLoadingTransport(false);
      }
    };

    fetchTransportAllocation();
  }, [enrollmentId, studentId]);

  useEffect(() => {
    const fetchAcademicDetails = async () => {
      if (!enrollmentId) {
        setFeeTypes([]);
        setDiscount(null);
        return;
      }

      setLoadingAcademicDetails(true);
      try {
        const [feesResponse, discountResponse] = await Promise.all([
          classId && sessionId && systemId
            ? getAllFeeType({
                page: 1,
                limit: 100,
                classes: classId,
                session: sessionId,
                system: systemId,
                status: "active",
              })
            : Promise.resolve({ data: [], pagination: null }),
          getDiscountByEnrollment(enrollmentId).catch(() => null),
        ]);

        setFeeTypes(feesResponse.data || []);
        setDiscount(discountResponse?.data?.discount || null);
      } catch (error) {
        console.error("Error fetching student academic details:", error);
        setFeeTypes([]);
        setDiscount(null);
      } finally {
        setLoadingAcademicDetails(false);
      }
    };

    fetchAcademicDetails();
  }, [enrollmentId, classId, sessionId, systemId]);

  useEffect(() => {
    const fetchSectionAndGroupOptions = async () => {
      if (!showEditProfile || !classId) return;
      try {
        const [classesResponse, groupsResponse] = await Promise.all([
          getAllClasses({ class: classId, session: sessionId, limit: 1 }),
          systemId ? listGroups({ system: systemId, class: classId }) : Promise.resolve({ data: [] }),
        ]);
        const sections = classesResponse.classes?.[0]?.sections || [];
        setSectionOptions(sections.map((section: any) => ({ value: section._id, label: section.name })));
        setGroupOptions(
          (groupsResponse.data || []).map((group: any) => ({ value: group._id, label: group.name }))
        );
      } catch (error) {
        console.error("Error fetching section/group options:", error);
        setSectionOptions([]);
        setGroupOptions([]);
      }
    };

    fetchSectionAndGroupOptions();
  }, [showEditProfile, classId, sessionId, systemId]);

  useEffect(() => {
    if (!showEditProfile || areaOptions.length) return;
    getAreas()
      .then(setAreaOptions)
      .catch(() => setAreaOptions([]));
  }, [showEditProfile, areaOptions.length]);

  const getVanRegistration = (van: any): string => {
    return (
      van?.registration_number ||
      van?.registrationNo ||
      van?.registration_no ||
      van?.registration ||
      "-"
    );
  };

  const getVanType = (van: any): string => {
    return van?.van_type || van?.vanType || van?.type || "-";
  };

  const formatCurrency = (amount?: number | string | null): string => {
    const value = Number(amount || 0);
    if (!value) return "N/A";
    return value.toLocaleString("en-PK", {
      style: "currency",
      currency: "PKR",
      maximumFractionDigits: 0,
    });
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return "N/A";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
  };

  const formatDateInput = (value?: string | null): string => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  };

  const displayValue = (value?: string | number | null): string => {
    if (value === 0) return "0";
    return value ? String(value) : "N/A";
  };

  const formatBoolean = (value?: boolean | null): string => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "N/A";
  };

  const formatList = (value?: string[] | null): string => {
    return value?.length ? value.filter(Boolean).join(", ") : "N/A";
  };

  const fullName = (user?: { firstName?: string; lastName?: string } | null, fallback?: string) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || fallback || "N/A";

  const openEditProfile = () => {
    if (!data) return;
    setEditForm({
      firstName: data.user?.firstName || data.admission?.firstName || "",
      lastName: data.user?.lastName || data.admission?.lastName || "",
      email: data.user?.email || data.admission?.email || "",
      mobileNO: data.admission?.mobileNO || data.user?.phoneNumber || "",
      gender: data.admission?.gender || data.user?.gender || "",
      dateOfBirth: formatDateInput(data.admission?.dateOfBirth || data.student?.dateOfBirth),
      admissionDate: formatDateInput(data.admission?.admissionDate),
      section: data.section?._id || "",
      group: data.group?._id || "",
      bloodGroup: data.admission?.bloodGroup || data.user?.bloodGroup || "",
      religion: data.admission?.religion || "",
      religionType: data.admission?.religionType || "",
      religionTypeOther: data.admission?.religionTypeOther || "",
      nationality: data.admission?.nationality || "",
      bFormNo: data.admission?.bFormNo || "",
      placeOfBirth: data.admission?.placeOfBirth || "",
      motherTongue: data.admission?.motherTongue || data.student?.motherTongue || "",
      languageKnown: (data.admission?.languageKnown || []).join(", "),
      hasSiblings: data.admission?.hasSiblings ?? false,
      noOfSiblings: data.admission?.noOfSiblings ?? "",
      noOfSiblingsEnrolled: data.admission?.noOfSiblingsEnrolled ?? "",
      prevSchoolName: data.admission?.prevSchoolName || "",
      prevSchoolAddress: data.admission?.prevSchoolAddress || "",
      prevClass: data.admission?.prevClass || "",
      lastResult: data.admission?.lastResult || "",
      emergencyContactRelation: data.admission?.emergencyContactRelation || data.user?.emergencyContactRelation || "",
      fatherName: data.admission?.fatherName || fullName(data.father?.user, ""),
      fatherPhoneNumber: data.admission?.fatherPhoneNumber || data.father?.user?.phoneNumber || "",
      fatherEmail: data.admission?.fatherEmail || data.father?.user?.email || "",
      fatherCnic: data.admission?.fatherCnic || data.father?.cnic || "",
      fatherOccupation: data.admission?.fatherOccupation || "",
      fatherCompanyName: data.admission?.fatherCompanyName || "",
      fatherSalary: data.admission?.fatherSalary ?? "",
      isFatherVaccinated: data.admission?.isFatherVaccinated ?? false,
      motherName: data.admission?.motherName || fullName(data.mother?.user, ""),
      motherPhoneNumber: data.admission?.motherPhoneNumber || data.mother?.user?.phoneNumber || "",
      motherEmail: data.admission?.motherEmail || data.mother?.user?.email || "",
      motherCnic: data.admission?.motherCnic || data.mother?.cnic || "",
      motherOccupation: data.admission?.motherOccupation || "",
      motherCompanyName: data.admission?.motherCompanyName || "",
      motherSalary: data.admission?.motherSalary ?? "",
      isMotherVaccinated: data.admission?.isMotherVaccinated ?? false,
      guardianName: data.admission?.guardianName || fullName(data.guardian?.user, ""),
      guardianRelation: data.admission?.guardianRelation || "",
      guardianPhoneNumber: data.admission?.guardianPhoneNumber || data.guardian?.user?.phoneNumber || "",
      guardianEmail: data.admission?.guardianEmail || data.guardian?.user?.email || "",
      guardianCnic: data.admission?.guardianCnic || data.guardian?.cnic || "",
      guardianOccupation: data.admission?.guardianOccupation || "",
      guardianCompanyName: data.admission?.guardianCompanyName || "",
      guardianSalary: data.admission?.guardianSalary ?? "",
      isGuardianVaccinated: data.admission?.isGuardianVaccinated ?? false,
      houseNo: data.admission?.houseNo || "",
      buildingName: data.admission?.buildingName || "",
      blockNo: data.admission?.blockNo || "",
      area: data.admission?.area || "",
      city: data.admission?.city || "",
      sameAsCurrentAddress: data.admission?.sameAsCurrentAddress ?? true,
      permanentHouseNo: data.admission?.permanentHouseNo || "",
      permanentBuildingName: data.admission?.permanentBuildingName || "",
      permanentBlockNo: data.admission?.permanentBlockNo || "",
      permanentArea: data.admission?.permanentArea || "",
      permanentCity: data.admission?.permanentCity || "",
      hasMedicalIssue: data.admission?.hasMedicalIssue ?? data.student?.hasMedicalIssue ?? false,
      allergies: data.admission?.allergies || data.student?.allergies || "",
      medications: data.admission?.medications || data.student?.medications || "",
      height: data.admission?.height || data.student?.height || "",
      weight: data.admission?.weight || data.student?.weight || "",
      isVaccinated: data.admission?.isVaccinated ?? false,
    });
    setShowEditProfile(true);
  };

  const updateEditField = (field: string, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveEditProfile = async () => {
    if (!enrollmentId) return;
    setSavingProfile(true);
    try {
      const payload = {
        ...editForm,
        phoneNumber: editForm.mobileNO,
        languageKnown: String(editForm.languageKnown || "")
          .split(",")
          .map((item: string) => item.trim())
          .filter(Boolean),
      };
      await updateEnrollmentProfile(enrollmentId, payload);
      await refetch();
      setShowEditProfile(false);
      toast.success("Student profile updated successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update student profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const sessionName =
    data?.session?.title ||
    data?.session?.name ||
    (data?.session?.startDate && data?.session?.endDate
      ? `${formatDate(data.session.startDate)} - ${formatDate(data.session.endDate)}`
      : "N/A");

  const formatDiscount = (): string => {
    const source = discount || data?.discounts?.[0];
    if (!source?.discount) return "No discount";
    return source.discountType === "percentage"
      ? `${source.discount}%`
      : formatCurrency(source.discount);
  };

  const monthlyFeeTypes = feeTypes.filter((fee) => {
    const frequency = String(fee.frequency || "").toLowerCase();
    const name = String(fee.name || "").toLowerCase();
    return frequency === "monthly" || name.includes("monthly") || name.includes("tuition");
  });
  const monthlyFeeTotal = monthlyFeeTypes.reduce(
    (total, fee) => total + (Number(fee.amount) || 0),
    0
  );
  const studentDetailItems = [
    { label: "GR Number", value: data?.student?.grNumber },
    { label: "System", value: data?.system?.name },
    { label: "Class", value: data?.class?.name },
    { label: "Section", value: data?.section?.name },
    { label: "Group", value: data?.group?.name },
    { label: "Session", value: sessionName },
    { label: "Campus", value: data?.branch?.name },
    { label: "Status", value: data?.status },
    { label: "Gender", value: data?.gender || data?.user?.gender || data?.admission?.gender },
    { label: "Date of Birth", value: formatDate(data?.student?.dateOfBirth || data?.admission?.dateOfBirth) },
    { label: "Admission Date", value: formatDate(data?.admission?.admissionDate || data?.createdAt) },
    { label: "Email", value: data?.user?.email || data?.admission?.email },
    { label: "Phone Number", value: data?.user?.phoneNumber || data?.admission?.mobileNO },
    { label: "B-Form / CNIC", value: data?.admission?.bFormNo },
    { label: "Nationality", value: data?.admission?.nationality },
    { label: "Place of Birth", value: data?.admission?.placeOfBirth },
    { label: "Blood Group", value: data?.user?.bloodGroup || data?.admission?.bloodGroup },
    { label: "Religion", value: data?.admission?.religion },
    { label: "Religion Type", value: data?.admission?.religionTypeOther || data?.admission?.religionType },
    { label: "Mother Tongue", value: data?.admission?.motherTongue || data?.student?.motherTongue },
    { label: "Languages Known", value: formatList(data?.admission?.languageKnown) },
    { label: "Temporary GR", value: data?.admission?.temporaryGR },
    { label: "Has Siblings", value: formatBoolean(data?.admission?.hasSiblings) },
    { label: "No. of Siblings", value: data?.admission?.noOfSiblings ?? "N/A" },
    { label: "Siblings in MPA", value: data?.admission?.noOfSiblingsEnrolled ?? "N/A" },
    { label: "Previous School", value: data?.admission?.prevSchoolName },
    { label: "Previous School Address", value: data?.admission?.prevSchoolAddress },
    { label: "Previous Class", value: data?.admission?.prevClass },
    { label: "Last Result", value: data?.admission?.lastResult },
    {
      label: "Monthly Fees",
      value: loadingAcademicDetails
        ? "Loading..."
        : monthlyFeeTotal
          ? formatCurrency(monthlyFeeTotal)
          : "N/A",
    },
    { label: "Discount", value: loadingAcademicDetails ? "Loading..." : formatDiscount() },
  ];

  const parentDetailRows = [
    {
      key: "father",
      label: "Father",
      image: "assets/img/parents/parent-13.jpg",
      picture: data?.admission?.fatherPicture,
      name: fullName(data?.father?.user, data?.admission?.fatherName),
      phone: data?.father?.user?.phoneNumber || data?.admission?.fatherPhoneNumber,
      email: data?.father?.user?.email || data?.admission?.fatherEmail,
      cnic: data?.father?.cnic || data?.admission?.fatherCnic,
      occupation: data?.admission?.fatherOccupation,
      company: data?.admission?.fatherCompanyName,
      salary: data?.admission?.fatherSalary,
    },
    {
      key: "mother",
      label: "Mother",
      image: "assets/img/parents/parent-14.jpg",
      picture: data?.admission?.motherPicture,
      name: fullName(data?.mother?.user, data?.admission?.motherName),
      phone: data?.mother?.user?.phoneNumber || data?.admission?.motherPhoneNumber,
      email: data?.mother?.user?.email || data?.admission?.motherEmail,
      cnic: data?.mother?.cnic || data?.admission?.motherCnic,
      occupation: data?.admission?.motherOccupation,
      company: data?.admission?.motherCompanyName,
      salary: data?.admission?.motherSalary,
    },
    {
      key: "guardian",
      label: data?.admission?.guardianRelation || "Guardian",
      image: "assets/img/parents/parent-13.jpg",
      picture: data?.admission?.guardianPicture,
      name: fullName(data?.guardian?.user, data?.admission?.guardianName),
      phone: data?.guardian?.user?.phoneNumber || data?.admission?.guardianPhoneNumber,
      email: data?.guardian?.user?.email || data?.admission?.guardianEmail,
      cnic: data?.guardian?.cnic || data?.admission?.guardianCnic,
      occupation: data?.admission?.guardianOccupation,
      company: data?.admission?.guardianCompanyName,
      salary: data?.admission?.guardianSalary,
    },
  ].filter(parent => parent.name !== "N/A" || parent.phone || parent.email || parent.cnic);

  const emergencyRelation = data?.admission?.emergencyContactRelation || data?.user?.emergencyContactRelation;
  const emergencySource = parentDetailRows.find(
    parent => parent.key === String(emergencyRelation || "").toLowerCase()
  );
  const emergencyContactItems = [
    { label: "Relation", value: emergencyRelation },
    { label: "Name", value: emergencySource?.name },
    { label: "Phone", value: emergencySource?.phone },
    { label: "Email", value: emergencySource?.email },
    { label: "CNIC", value: emergencySource?.cnic },
  ];

  const medicalItems = [
    { label: "Has Medical Issue", value: formatBoolean(data?.admission?.hasMedicalIssue ?? data?.student?.hasMedicalIssue) },
    { label: "Known Allergies", value: data?.admission?.allergies || data?.student?.allergies || "None" },
    { label: "Medications", value: data?.admission?.medications || data?.student?.medications },
    { label: "Height", value: data?.admission?.height || data?.student?.height },
    { label: "Weight", value: data?.admission?.weight || data?.student?.weight },
    { label: "Student Vaccinated", value: formatBoolean(data?.admission?.isVaccinated) },
    { label: "Father Vaccinated", value: formatBoolean(data?.admission?.isFatherVaccinated) },
    { label: "Mother Vaccinated", value: formatBoolean(data?.admission?.isMotherVaccinated) },
    { label: "Guardian Vaccinated", value: formatBoolean(data?.admission?.isGuardianVaccinated) },
  ];

  const editSections = [
    {
      title: "Student Information",
      fields: [
        { label: "GR Number", name: "grNumber", value: data?.student?.grNumber || "", disabled: true },
        { label: "Class", name: "className", value: data?.class?.name || "", disabled: true },
        { label: "Section", name: "section", type: "select", options: sectionOptions },
        { label: "Group", name: "group", type: "select", options: groupOptions },
        { label: "First Name", name: "firstName" },
        { label: "Last Name", name: "lastName" },
        { label: "Email", name: "email", type: "email" },
        { label: "Phone Number", name: "mobileNO" },
        { label: "Gender", name: "gender", type: "select", options: ["male", "female", "other"] },
        { label: "Date of Birth", name: "dateOfBirth", type: "date" },
        { label: "Admission Date", name: "admissionDate", type: "date" },
        { label: "Blood Group", name: "bloodGroup" },
        { label: "Religion", name: "religion" },
        { label: "Religion Type", name: "religionType" },
        { label: "Religion Type Other", name: "religionTypeOther" },
        { label: "Nationality", name: "nationality" },
        { label: "B-Form / CNIC", name: "bFormNo" },
        { label: "Place of Birth", name: "placeOfBirth" },
        { label: "Mother Tongue", name: "motherTongue" },
        { label: "Languages Known", name: "languageKnown" },
        { label: "Has Siblings", name: "hasSiblings", type: "checkbox" },
        { label: "No. of Siblings", name: "noOfSiblings", type: "number" },
        { label: "Siblings in MPA", name: "noOfSiblingsEnrolled", type: "number" },
        { label: "Previous School", name: "prevSchoolName" },
        { label: "Previous School Address", name: "prevSchoolAddress" },
        { label: "Previous Class", name: "prevClass" },
        { label: "Last Result", name: "lastResult" },
      ],
    },
    {
      title: "Parents Information",
      fields: [
        { label: "Father Name", name: "fatherName" },
        { label: "Father Phone", name: "fatherPhoneNumber" },
        { label: "Father Email", name: "fatherEmail", type: "email" },
        { label: "Father CNIC", name: "fatherCnic" },
        { label: "Father Occupation", name: "fatherOccupation" },
        { label: "Father Company", name: "fatherCompanyName" },
        { label: "Father Salary", name: "fatherSalary", type: "number" },
        { label: "Father Vaccinated", name: "isFatherVaccinated", type: "checkbox" },
        { label: "Mother Name", name: "motherName" },
        { label: "Mother Phone", name: "motherPhoneNumber" },
        { label: "Mother Email", name: "motherEmail", type: "email" },
        { label: "Mother CNIC", name: "motherCnic" },
        { label: "Mother Occupation", name: "motherOccupation" },
        { label: "Mother Company", name: "motherCompanyName" },
        { label: "Mother Salary", name: "motherSalary", type: "number" },
        { label: "Mother Vaccinated", name: "isMotherVaccinated", type: "checkbox" },
        { label: "Guardian Name", name: "guardianName" },
        { label: "Guardian Relation", name: "guardianRelation" },
        { label: "Guardian Phone", name: "guardianPhoneNumber" },
        { label: "Guardian Email", name: "guardianEmail", type: "email" },
        { label: "Guardian CNIC", name: "guardianCnic" },
        { label: "Guardian Occupation", name: "guardianOccupation" },
        { label: "Guardian Company", name: "guardianCompanyName" },
        { label: "Guardian Salary", name: "guardianSalary", type: "number" },
        { label: "Guardian Vaccinated", name: "isGuardianVaccinated", type: "checkbox" },
      ],
    },
    {
      title: "Emergency Contact",
      fields: [
        { label: "Emergency Contact Relation", name: "emergencyContactRelation", type: "select", options: ["father", "mother", "guardian"] },
      ],
    },
    {
      title: "Current Address",
      fields: [
        { label: "House No", name: "houseNo" },
        { label: "Building Name", name: "buildingName" },
        { label: "Block No", name: "blockNo" },
        { label: "Area", name: "area", type: "searchable-select", options: areaOptions },
        { label: "City", name: "city" },
      ],
    },
    {
      title: "Permanent Address",
      fields: [
        { label: "Same as Current Address", name: "sameAsCurrentAddress", type: "checkbox" },
        ...(editForm.sameAsCurrentAddress
          ? []
          : [
              { label: "House No", name: "permanentHouseNo" },
              { label: "Building Name", name: "permanentBuildingName" },
              { label: "Block No", name: "permanentBlockNo" },
              { label: "Area", name: "permanentArea", type: "searchable-select", options: areaOptions },
              { label: "City", name: "permanentCity" },
            ]),
      ],
    },
    {
      title: "Medical History",
      fields: [
        { label: "Has Medical Issue", name: "hasMedicalIssue", type: "checkbox" },
        { label: "Allergies", name: "allergies" },
        { label: "Medications", name: "medications" },
        { label: "Height", name: "height" },
        { label: "Weight", name: "weight" },
        { label: "Student Vaccinated", name: "isVaccinated", type: "checkbox" },
      ],
    },
  ];

  const renderEditField = (field: any) => {
    const value = field.disabled ? field.value : editForm[field.name];
    if (field.type === "checkbox") {
      return (
        <div className="form-check form-switch mt-4">
          <input
            className="form-check-input"
            type="checkbox"
            checked={Boolean(value)}
            disabled={field.disabled}
            onChange={(event) => updateEditField(field.name, event.target.checked)}
          />
          <label className="form-check-label">{field.label}</label>
        </div>
      );
    }
    if (field.type === "searchable-select") {
      // Only for long option lists (e.g. Area's 224 entries) where a plain
      // <select> is unusable — everything else stays a native dropdown.
      const options: SelectOption[] = (field.options || []).map((option: string | SelectOption) =>
        typeof option === "string" ? { value: option, label: option } : option
      );
      // Some records store the option's label text (e.g. "North Nazimabad")
      // instead of its slug value ("north-nazimabad") — match either so the
      // dropdown still shows the current selection instead of blank.
      const norm = (s: any) => String(s ?? "").trim().toLowerCase();
      const selected =
        options.find(
          (option) => norm(option.value) === norm(value) || norm(option.label) === norm(value)
        ) || null;
      return (
        <>
          <label className="form-label">{field.label}</label>
          <CommonSelect
            className="select"
            options={options}
            value={selected}
            getOptionValue={(option: SelectOption) => option.value}
            getOptionLabel={(option: SelectOption) => option.label}
            isDisabled={field.disabled}
            placeholder="Select"
            onChange={(selectedOption: SelectOption | null) =>
              updateEditField(field.name, selectedOption?.value || "")
            }
          />
        </>
      );
    }
    if (field.type === "select") {
      const options: SelectOption[] = (field.options || []).map((option: string | SelectOption) =>
        typeof option === "string" ? { value: option, label: option } : option
      );
      // Match case/whitespace-insensitively (same as searchable-select above) so a
      // saved value like "Father" or " father " still pre-selects the "father" option
      // instead of silently falling back to the blank placeholder.
      const norm = (s: any) => String(s ?? "").trim().toLowerCase();
      const matched = options.find(
        (option) => norm(option.value) === norm(value) || norm(option.label) === norm(value)
      );
      return (
        <>
          <label className="form-label">{field.label}</label>
          <select
            className="form-select"
            value={matched ? matched.value : ""}
            disabled={field.disabled}
            onChange={(event) => updateEditField(field.name, event.target.value)}
          >
            <option value="">Select</option>
            {options.map((option) => (
              <option value={option.value} key={option.value}>{option.label}</option>
            ))}
          </select>
        </>
      );
    }
    return (
      <>
        <label className="form-label">{field.label}</label>
        <input
          className="form-control"
          type={field.type || "text"}
          value={value ?? ""}
          disabled={field.disabled}
          onChange={(event) => updateEditField(field.name, event.target.value)}
        />
      </>
    );
  };

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          {/* Student Information */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5>Student Information</h5>
              {canEditProfile && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={openEditProfile}
                >
                  <i className="ti ti-edit me-1" />
                  Edit Profile
                </button>
              )}
            </div>
            <div className="card-body pb-1">
              <div className="row">
                {studentDetailItems.map((item) => (
                  <div className="col-sm-6 col-lg-3" key={item.label}>
                    <div className="mb-3 overflow-hidden">
                      <p className="text-dark fw-medium mb-1">{item.label}</p>
                      <p className="mb-0 text-truncate" title={String(item.value || "N/A")}>
                        {item.value || "N/A"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* /Student Information */}

          {/* Parents Information */}
          <div className="card">
            <div className="card-header">
              <h5>Parents Information</h5>
            </div>
            <div className="card-body">
              {parentDetailRows.map((parent) => (
                <div className="border rounded p-3 pb-0 mb-3" key={parent.key}>
                  <div className="row">
                    <div className="col-lg-3 col-sm-6">
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-lg flex-shrink-0">
                          <ImageWithBasePath
                            fullSrc={parent.picture}
                            src={parent.image}
                            className="img-fluid rounded"
                            alt={`${parent.label} Image`}
                          />
                        </span>
                        <div className="ms-2 overflow-hidden">
                          <h6 className="text-truncate">{parent.name}</h6>
                          <p className="text-primary">{parent.label}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">Phone</p>
                        <p>{displayValue(parent.phone)}</p>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="mb-3 overflow-hidden">
                        <p className="text-dark fw-medium mb-1">Email</p>
                        <p className="text-truncate" title={displayValue(parent.email)}>
                          {displayValue(parent.email)}
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">CNIC</p>
                        <p>{displayValue(parent.cnic)}</p>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">Occupation</p>
                        <p>{displayValue(parent.occupation)}</p>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">Company</p>
                        <p>{displayValue(parent.company)}</p>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">Salary</p>
                        <p>{parent.salary ? formatCurrency(parent.salary) : "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {!parentDetailRows.length && (
                <p className="text-muted mb-0">No parent/guardian records linked to this student.</p>
              )}
            </div>
          </div>
          {/* /Parents Information */}

          {/* Emergency Contact */}
          <div className="card">
            <div className="card-header">
              <h5 className="text-danger">Emergency Contact</h5>
            </div>
            <div className="card-body pb-1">
              <div className="row">
                {emergencyContactItems.map((item) => (
                  <div className="col-sm-6 col-lg-3" key={item.label}>
                    <div className="mb-3 overflow-hidden">
                      <p className="text-dark fw-medium mb-1">{item.label}</p>
                      <p className="mb-0 text-truncate" title={displayValue(item.value)}>
                        {displayValue(item.value)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* /Emergency Contact */}
        </div>

        {/* Documents */}
        {/* <div className="col-xxl-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <h5>Documents</h5>
                    </div>
                    <div className="card-body">
                      <div className="bg-light-300 border rounded d-flex align-items-center justify-content-between mb-3 p-2">
                        <div className="d-flex align-items-center overflow-hidden">
                          <span className="avatar avatar-md bg-white rounded flex-shrink-0 text-default">
                            <i className="ti ti-pdf fs-15" />
                          </span>
                          <div className="ms-2">
                            <p className="text-truncate fw-medium text-dark">
                              BirthCertificate.pdf
                            </p>
                          </div>
                        </div>
                        <Link to="#" className="btn btn-dark btn-icon btn-sm">
                          <i className="ti ti-download" />
                        </Link>
                      </div>
                      <div className="bg-light-300 border rounded d-flex align-items-center justify-content-between p-2">
                        <div className="d-flex align-items-center overflow-hidden">
                          <span className="avatar avatar-md bg-white rounded flex-shrink-0 text-default">
                            <i className="ti ti-pdf fs-15" />
                          </span>
                          <div className="ms-2">
                            <p className="text-truncate fw-medium text-dark">
                              Transfer Certificate.pdf
                            </p>
                          </div>
                        </div>
                        <Link to="#" className="btn btn-dark btn-icon btn-sm">
                          <i className="ti ti-download" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div> */}
        {/* /Documents */}

        {/* Address */}
        <div className="col-xxl-6 d-flex">
          <div className="card flex-fill">
            <div className="card-header">
              <h5>Address</h5>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-start mb-3">
                <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                  <i className="ti ti-map-pin-up" />
                </span>
                <div className="flex-fill">
                  <p className="text-dark fw-medium mb-2">Current Address</p>
                  <div className="row">
                    {[
                      { label: "House No", value: data?.admission?.houseNo },
                      { label: "Building Name", value: data?.admission?.buildingName },
                      { label: "Block No", value: data?.admission?.blockNo },
                      { label: "Area", value: data?.admission?.area },
                      { label: "City", value: data?.admission?.city },
                    ].map(field => (
                      <div className="col-6 col-md-4 mb-2" key={field.label}>
                        <p className="mb-0 fs-12 text-muted">{field.label}</p>
                        <p className="mb-0">{field.value || "N/A"}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-start">
                <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                  <i className="ti ti-map-pins" />
                </span>
                <div className="flex-fill">
                  <p className="text-dark fw-medium mb-2">Permanent Address</p>
                  {data?.admission?.sameAsCurrentAddress !== false ? (
                    <p className="text-muted mb-0">Same as current address</p>
                  ) : (
                    <div className="row">
                      {[
                        { label: "House No", value: data?.admission?.permanentHouseNo },
                        { label: "Building Name", value: data?.admission?.permanentBuildingName },
                        { label: "Block No", value: data?.admission?.permanentBlockNo },
                        { label: "Area", value: data?.admission?.permanentArea },
                        { label: "City", value: data?.admission?.permanentCity },
                      ].map(field => (
                        <div className="col-6 col-md-4 mb-2" key={field.label}>
                          <p className="mb-0 fs-12 text-muted">{field.label}</p>
                          <p className="mb-0">{field.value || "N/A"}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Address */}

        {/* Previous School Details */}
        {/* <div className="col-xxl-12">
                  <div className="card">
                    <div className="card-header">
                      <h5>Previous School Details</h5>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <p className="text-dark fw-medium mb-1">
                              Previous School Name
                            </p>
                            <p>Oxford Matriculation, USA</p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <p className="text-dark fw-medium mb-1">
                              School Address
                            </p>
                            <p>1852 Barnes Avenue, Cincinnati, OH 45202</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
        {/* /Previous School Details */}

        {/* Bank Details */}
        {/* <div className="col-xxl-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <h5>Bank Details</h5>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-3">
                            <p className="text-dark fw-medium mb-1">
                              Bank Name
                            </p>
                            <p>Bank of America</p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <p className="text-dark fw-medium mb-1">Campus</p>
                            <p>Cincinnati</p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <p className="text-dark fw-medium mb-1">IFSC</p>
                            <p>BOA83209832</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
        {/* /Bank Details */}

        {/* Medical History */}
        <div className="col-xxl-6 d-flex">
          <div className="card flex-fill">
            <div className="card-header">
              <h5>Medical History</h5>
            </div>
            <div className="card-body pb-1">
              <div className="row">
                {medicalItems.map((item) => (
                  <div className="col-sm-6 col-lg-4" key={item.label}>
                    <div className="mb-3 overflow-hidden">
                      <p className="text-dark fw-medium mb-1">{item.label}</p>
                      <p className="mb-0 text-truncate" title={displayValue(item.value)}>
                        {displayValue(item.value)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* /Medical History */}

        {/* Transport Allocation */}
        <div className="col-xxl-6 d-flex">
          <div className="card flex-fill">
            <div className="card-header">
              <h5>Transport Allocation</h5>
            </div>
            <div className="card-body pb-1">
              {loadingTransport ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm text-primary me-2" />
                  <span className="text-muted">Loading...</span>
                </div>
              ) : transportAllocation ? (
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <p className="text-dark fw-medium mb-1">Vehicle</p>
                      <p className="mb-0">
                        {getVanRegistration(transportAllocation.van) !== "-"
                          ? getVanRegistration(transportAllocation.van)
                          : getVanType(transportAllocation.van) || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <p className="text-dark fw-medium mb-1">Driver</p>
                      <p className="mb-0">
                        {(transportAllocation.driver as any)?.first_name ||
                          (transportAllocation.driver as any)?.firstName
                          ? `${(transportAllocation.driver as any).first_name || (transportAllocation.driver as any).firstName} ${(transportAllocation.driver as any).last_name || (transportAllocation.driver as any).lastName || ""}`.trim()
                          : (transportAllocation.driver as any)?.driverID ||
                            (transportAllocation.driver as any)?._id ||
                            "-"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <p className="text-dark fw-medium mb-1">Pickup / Drop Point</p>
                      <p className="mb-0">{transportAllocation.pickupStop || "-"}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <p className="text-dark fw-medium mb-1">Status</p>
                      <span
                        className={`badge ${
                          transportAllocation.status === "active"
                            ? "bg-success"
                            : "bg-secondary"
                        }`}
                      >
                        {transportAllocation.status || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">No transport assigned</p>
              )}
            </div>
          </div>
        </div>
        {/* /Transport Allocation */}

        {/* Other Info */}
        {/* <div className="col-xxl-12">
                  <div className="card">
                    <div className="card-header">
                      <h5>Other Info</h5>
                    </div>
                    <div className="card-body">
                      <p>
                        Depending on the specific needs of your organization or
                        system, additional information may be collected or
                        tracked. It's important to ensure that any data
                        collected complies with privacy regulations and policies
                        to protect students' sensitive information.
                      </p>
                    </div>
                  </div>
                </div> */}
        {/* /Other Info */}
      </div>

      {showEditProfile && canEditProfile && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Student Profile</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowEditProfile(false)}
                    disabled={savingProfile}
                  />
                </div>
                <div className="modal-body edit-student-profile-body">
                  {editSections.map((section) => (
                    <div
                      className={`card mb-3 edit-profile-section ${
                        ["Emergency Contact", "Permanent Address"].includes(section.title)
                          ? "edit-profile-problem-section"
                          : ""
                      }`}
                      key={section.title}
                    >
                      <div className="card-header edit-profile-section-header text-center">
                        <h5
                          className={`mb-0 fs-18 fw-bold text-decoration-underline ${
                            section.title === "Emergency Contact" ? "text-danger" : ""
                          }`}
                        >
                          {section.title}
                        </h5>
                      </div>
                      <div className="card-body edit-profile-section-body">
                        <div className="row g-3 edit-profile-field-row">
                          {section.fields.map((field: any) => (
                            <div
                              className={`${
                                section.title === "Emergency Contact"
                                  ? "col-12 col-md-12"
                                  : "col-12 col-md-6 col-lg-4"
                              } edit-profile-field-col`}
                              key={field.name}
                            >
                              {renderEditField(field)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowEditProfile(false)}
                    disabled={savingProfile}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveEditProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

export default StudentDetails;
